import React, { useState, useEffect, useRef } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { getEventi, saveEvento, deleteEvento } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelBody, PanelHead, Button, Spinner } from '../components/ui';
import { callFunction } from '../utils/cloudFunctions';

// ─── Upload immagine evento ───────────────────────────────────────────────────
async function uploadEventoImage(beachCode, file, onProgress) {
    const storage = getStorage();
    const ext = file.name.split('.').pop();
    const path = `lidi/${beachCode}/eventi/${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    return new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, file);
        task.on('state_changed',
            snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
            reject,
            async () => { resolve({ url: await getDownloadURL(task.snapshot.ref), path }); }
        );
    });
}

async function deleteEventoImage(path) {
    if (!path) return;
    try {
        const storage = getStorage();
        await deleteObject(ref(storage, path));
    } catch (e) { /* già eliminata */ }
}

const TAGS = ['Musica live', 'Aperitivo', 'Pranzo', 'Cena', 'Degustazione', 'Serata a tema', 'Sport', 'Famiglia', 'Torneo', 'Altro'];

const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)',
    width: '100%', boxSizing: 'border-box',
};

// ─── Form evento ──────────────────────────────────────────────────────────────
function EventoForm({ evento, onSave, onCancel, saving }) {
    const isNew = !evento.id;
    const [form, setForm] = useState({
        titolo: evento.titolo || '',
        descrizione: evento.descrizione || '',
        data: evento.data || '',
        ora: evento.ora || '',
        tag: evento.tag || '',
        immagineUrl: evento.immagineUrl || '',
        immaginePath: evento.immaginePath || '',
        attivo: evento.attivo !== false,
        // Default ON solo per evento nuovo: in modifica rispetta la scelta originale.
        // Se la modifica re-attiva la flag, NON re-invia push (la function triggera solo onCreate).
        inviaNotificaPush: evento.id ? (evento.inviaNotificaPush !== false) : true,
        // Default OFF: il gestore deve scegliere esplicitamente di abilitarlo
        // (decisione del lido, non derivata dalla categoria evento).
        prenotabilePerTelefono: evento.prenotabilePerTelefono === true,
    });
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileRef = useRef(null);
    const { admin } = useAuth();

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Seleziona un file immagine'); return; }
        if (file.size > 5 * 1024 * 1024) { alert('Immagine troppo grande (max 5MB)'); return; }
        setUploading(true);
        try {
            // Elimina la vecchia immagine se esiste
            if (form.immaginePath) await deleteEventoImage(form.immaginePath);
            const result = await uploadEventoImage(admin.beachCode, file, setUploadProgress);
            setForm(p => ({ ...p, immagineUrl: result.url, immaginePath: result.path }));
        } catch (err) {
            alert('Errore upload immagine');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleRemoveImage = async () => {
        if (!window.confirm("Rimuovere l'immagine ? ")) return;
    await deleteEventoImage(form.immaginePath);
        setForm(p => ({ ...p, immagineUrl: '', immaginePath: '' }));
    };

    const setF = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    return (
        <div style={{
            background: 'var(--navy-mid)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '16px',
        }}>
            <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '20px', fontFamily: 'var(--font-serif)' }}>
                {isNew ? 'Nuovo evento' : `Modifica — ${evento.titolo}`}
            </div>

            {/* Titolo */}
            <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Titolo *
                </label>
                <input placeholder="es. Serata Jazz al tramonto" value={form.titolo} onChange={setF('titolo')} style={inputStyle} />
            </div>

            {/* Data + Ora + Tag */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Data *
                    </label>
                    <input type="date" value={form.data} onChange={setF('data')} style={inputStyle} />
                </div>
                <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Ora
                    </label>
                    <input type="time" value={form.ora} onChange={setF('ora')} style={inputStyle} />
                </div>
                <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Categoria
                    </label>
                    <select value={form.tag} onChange={setF('tag')} style={inputStyle}>
                        <option value="">Seleziona...</option>
                        {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
            </div>

            {/* Descrizione */}
            <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Descrizione
                </label>
                <textarea
                    rows={3}
                    placeholder="Descrivi l'evento ai tuoi clienti..."
                    value={form.descrizione}
                    onChange={setF('descrizione')}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                />
            </div>

            {/* Immagine upload */}
            <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Immagine evento (opzionale)
                </label>

                {/* Preview immagine esistente */}
                {form.immagineUrl && (
                    <div style={{ position: 'relative', marginBottom: '10px', borderRadius: '10px', overflow: 'hidden' }}>
                        <img src={form.immagineUrl} alt="preview"
                            style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', display: 'block' }}
                        />
                        <button
                            onClick={handleRemoveImage}
                            style={{
                                position: 'absolute', top: '8px', right: '8px',
                                background: 'rgba(231,76,60,0.85)', border: 'none', borderRadius: '8px',
                                color: '#fff', cursor: 'pointer', padding: '6px 10px', fontSize: '12px',
                                fontFamily: 'var(--font-sans)',
                            }}
                        >
                            Rimuovi
                        </button>
                    </div>
                )}

                {/* Upload area */}
                {!form.immagineUrl && (
                    <div
                        onClick={() => !uploading && fileRef.current?.click()}
                        style={{
                            border: '2px dashed var(--border-mid)', borderRadius: 'var(--radius-md)',
                            padding: '24px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer',
                            background: 'var(--surface)', transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = 'var(--ocean)')}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-mid)')}
                    >
                        {uploading ? (
                            <div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                    Caricamento... {uploadProgress}%
                                </div>
                                <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--ocean)', transition: 'width 0.2s' }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🖼️</div>
                                <div style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '4px' }}>
                                    Clicca per caricare un'immagine
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                    JPG, PNG, WebP · Max 5MB
                                </div>
                            </>
                        )}
                    </div>
                )}

                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            </div>

            {/* Attivo */}
            <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
                <input type="checkbox" checked={form.attivo} onChange={e => setForm(p => ({ ...p, attivo: e.target.checked }))} />
                Evento visibile in app
            </label>

            {/* Prenotabile per telefono — mostra il CTA "Chiama per prenotare" nell'app */}
            <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '12px', lineHeight: 1.5 }}>
                <input
                    type="checkbox"
                    checked={form.prenotabilePerTelefono}
                    onChange={e => setForm(p => ({ ...p, prenotabilePerTelefono: e.target.checked }))}
                    style={{ marginTop: '2px' }}
                />
                <span>
                    📞 Prenotabile per telefono
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        I clienti vedranno un bottone "Chiama per prenotare" che apre il dialer col tuo numero.
                    </span>
                </span>
            </label>

            {/* Invia notifica push — solo alla CREAZIONE: in modifica la trigger Firestore non rifirerà */}
            {isNew && (
                <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '20px' }}>
                    <input type="checkbox" checked={form.inviaNotificaPush} onChange={e => setForm(p => ({ ...p, inviaNotificaPush: e.target.checked }))} />
                    Invia notifica push agli utenti del lido
                </label>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="primary" onClick={() => onSave(form)} disabled={!form.titolo || !form.data || saving}>
                    {saving ? <Spinner size={14} /> : isNew ? 'Crea evento' : 'Salva modifiche'}
                </Button>
                <Button variant="ghost" onClick={onCancel}>Annulla</Button>
            </div>
        </div>
    );
}

// ─── Card evento ──────────────────────────────────────────────────────────────
function EventoCard({ evento, beachCode, onEdit, onDelete }) {
    const today = new Date().toISOString().split('T')[0];
    const passato = evento.data < today;
    const [sending, setSending] = useState(false);

    const formatData = (d) => {
        const [y, m, g] = d.split('-');
        return `${g}/${m}/${y}`;
    };

    // Re-invio manuale della push: il trigger automatico parte solo al primo
    // create dell'evento, per ri-promuovere bisogna chiamare la function HTTP.
    // Il confirm dialog evita il "ops cliccato per sbaglio" — la notifica è
    // visibile a TUTTI gli utenti registrati al lido, conviene chiedere prima.
    const handleSendNotification = async () => {
        if (!window.confirm(`Inviare una notifica push agli utenti del lido per "${evento.titolo}"?`)) return;
        setSending(true);
        try {
            const result = await callFunction('sendManualNotification', {
                beachCode,
                type: 'event',
                id: evento.id,
            });
            alert(`Notifica inviata a ${result.sentTo}/${result.totalDevices} dispositivi.`);
        } catch (err) {
            alert(`Errore invio: ${err.message}`);
        } finally {
            setSending(false);
        }
    };

    return (
        <Panel style={{ marginBottom: '10px', opacity: passato ? 0.6 : 1 }}>
            <div style={{ display: 'flex', gap: '14px', padding: '14px 20px', alignItems: 'center' }}>

                {/* Immagine o placeholder */}
                <div style={{
                    width: '64px', height: '64px', borderRadius: '10px', flexShrink: 0,
                    background: evento.immagineUrl
                        ? `url(${evento.immagineUrl}) center/cover`
                        : 'var(--surface)',
                    border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {!evento.immagineUrl && <span style={{ fontSize: '24px' }}>🎉</span>}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text)' }}>{evento.titolo}</div>
                        {!evento.attivo && (
                            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                                Nascosto
                            </span>
                        )}
                        {passato && (
                            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>
                                Passato
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <span>📅 {formatData(evento.data)}{evento.ora && ` · ${evento.ora}`}</span>
                        {evento.tag && <span>🏷 {evento.tag}</span>}
                    </div>
                    {evento.descrizione && (
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {evento.descrizione}
                        </div>
                    )}
                </div>

                {/* Azioni */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {!passato && (
                        <Button size="sm" variant="ghost" onClick={handleSendNotification} disabled={sending}>
                            {sending ? <Spinner size={12} /> : '📣 Notifica'}
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onEdit}>Modifica</Button>
                    <Button size="sm" variant="danger" onClick={onDelete}>Elimina</Button>
                </div>
            </div>
        </Panel>
    );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function Eventi() {
    const { admin } = useAuth();
    const [eventi, setEventi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        if (!admin?.beachCode) return;
        getEventi(admin.beachCode)
            .then(setEventi)
            .finally(() => setLoading(false));
    }, [admin?.beachCode]);

    const handleSave = async (form) => {
        setSaving(true);
        try {
            const id = await saveEvento(admin.beachCode, editing?.id ? { ...form, id: editing.id } : form);
            const updated = editing?.id
                ? eventi.map(e => e.id === editing.id ? { ...form, id: editing.id } : e)
                : [...eventi, { ...form, id }];
            setEventi(updated.sort((a, b) => a.data.localeCompare(b.data)));
            setEditing(null);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Eliminare questo evento?')) return;
        await deleteEvento(admin.beachCode, id);
        setEventi(prev => prev.filter(e => e.id !== id));
    };

    const futuri = eventi.filter(e => e.data >= today);
    const passati = eventi.filter(e => e.data < today);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Topbar
                title="Eventi"
                subtitle="Pubblica eventi e serate speciali del tuo lido"
                actions={
                    <Button variant="primary" onClick={() => setEditing({})}>
                        + Nuovo evento
                    </Button>
                }
            />

            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                {editing !== null && (
                    <EventoForm
                        evento={editing}
                        onSave={handleSave}
                        onCancel={() => setEditing(null)}
                        saving={saving}
                    />
                )}

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                        <Spinner size={28} />
                    </div>
                ) : eventi.length === 0 && editing === null ? (
                    <Panel>
                        <PanelBody>
                            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎉</div>
                                <div style={{ fontSize: '13px', marginBottom: '16px' }}>
                                    Nessun evento creato.<br />
                                    Pubblica serate, concerti e appuntamenti speciali.
                                </div>
                                <Button variant="primary" onClick={() => setEditing({})}>
                                    + Crea primo evento
                                </Button>
                            </div>
                        </PanelBody>
                    </Panel>
                ) : (
                    <>
                        {futuri.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                                    Prossimi eventi — {futuri.length}
                                </div>
                                {futuri.map(e => (
                                    <EventoCard key={e.id} evento={e} beachCode={admin.beachCode} onEdit={() => setEditing(e)} onDelete={() => handleDelete(e.id)} />
                                ))}
                            </div>
                        )}

                        {passati.length > 0 && (
                            <div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                                    Eventi passati — {passati.length}
                                </div>
                                {passati.map(e => (
                                    <EventoCard key={e.id} evento={e} beachCode={admin.beachCode} onEdit={() => setEditing(e)} onDelete={() => handleDelete(e.id)} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}